import React, { useState } from 'react';
import {
  IonItem,
  IonLabel,
  IonList,
  IonToggle,
  IonListHeader,
  useIonToast
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { api } from '@/utils/api';
import { supabase } from '@/utils/supabase';
import { mediumImpact } from '@/utils/capacitorUtils';

interface GamePreferencesProps {
  gameId: string;
  preferences?: {
    showLeaderboard?: boolean;
    showLiveScores?: boolean;
    showStats?: boolean;
    autoAdvance?: boolean;
  };
  onUpdateSuccess?: () => void;
}

const GamePreferences: React.FC<GamePreferencesProps> = ({
  gameId,
  preferences = {},
  onUpdateSuccess
}) => {
  const { t } = useTranslation();
  const [presentToast] = useIonToast();

  // Default preferences if not provided
  const defaultPrefs = {
    showLeaderboard: true,
    showLiveScores: true,
    showStats: true,
    autoAdvance: false
  };

  // Merge provided preferences with defaults
  const [prefs, setPrefs] = useState({
    ...defaultPrefs,
    ...preferences
  });

  // Update a single preference
  const updatePreference = async (key: string, value: boolean) => {
    try {
      // Update local state
      setPrefs(prev => ({
        ...prev,
        [key]: value
      }));

      // Create the preferences object to update
      const updatedPreferences = {
        ...prefs,
        [key]: value
      };

      // Update preferences in the database
      await api.put(`/games/${gameId}/preferences`, {
        preferences: updatedPreferences
      });

      // Notify other players about the update
      await supabase
        .from('game_settings_updates')
        .insert({
          game_id: gameId,
          updated_at: new Date().toISOString()
        });

      mediumImpact();

      // Call success callback if provided
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }

      presentToast({
        message: t('preferences_updated'),
        duration: 2000,
        color: 'success'
      });
    } catch (error) {
      console.error('Error updating preferences:', error);
      presentToast({
        message: t('error_updating_preferences'),
        duration: 2000,
        color: 'danger'
      });
    }
  };

  return (
    <div>
      <IonListHeader>{t('display')}</IonListHeader>
      <IonList className="rounded-md overflow-hidden">
        <IonItem>
          <IonLabel>{t('show_leaderboard')}</IonLabel>
          <IonToggle
            checked={prefs.showLeaderboard}
            onIonChange={e => updatePreference('showLeaderboard', e.detail.checked)}
          />
        </IonItem>

        <IonItem>
          <IonLabel>{t('show_live_scores')}</IonLabel>
          <IonToggle
            checked={prefs.showLiveScores}
            onIonChange={e => updatePreference('showLiveScores', e.detail.checked)}
          />
        </IonItem>

        <IonItem>
          <IonLabel>{t('show_statistics')}</IonLabel>
          <IonToggle
            checked={prefs.showStats}
            onIonChange={e => updatePreference('showStats', e.detail.checked)}
          />
        </IonItem>
      </IonList>

      <IonListHeader className="mt-6">{t('gameplay')}</IonListHeader>
      <IonList className="rounded-md overflow-hidden">
        <IonItem lines="none">
          <IonLabel>{t('auto_change_hole')}</IonLabel>
          <IonToggle
            checked={prefs.autoAdvance}
            onIonChange={e => updatePreference('autoAdvance', e.detail.checked)}
          />
        </IonItem>
      </IonList>

      <div className="text-xs text-gray-500 mt-4 px-4">
        {t('preferences_sync_info')}
      </div>
    </div>
  );
};

export default GamePreferences;
